import { useQuery } from '@tanstack/react-query';
import { domainPurchasesApi } from '@/api/domainPurchases';
import { queryKeys } from '@/lib/queryKeys';
import { useApiMutation, type MutationCallbacks } from '@/hooks/useApiMutation';
import type { ConfirmPurchaseRequest, DomainPurchaseView, StartPurchaseRequest } from '@/types/api';

/** The purchases list, asked again every five seconds while one of them is under way. */
export function useDomainPurchases({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: queryKeys.domainPurchases.all,
    queryFn: domainPurchasesApi.overview,
    enabled,
    refetchInterval: (query) => (query.state.data?.purchases.some((purchase) => !purchase.finished) ? 5_000 : false),
  });
}

/**
 * One purchase, asked every two seconds while 2d2s works on it (ADR-0053):
 * each look writes what changed into its history, so the page follows it
 * live. Every fifteen while it waits for a confirmation — someone may confirm
 * or cancel it from another tab — and not once it is over.
 */
export function useDomainPurchase(id: string | null) {
  return useQuery({
    queryKey: queryKeys.domainPurchases.one(id ?? ''),
    queryFn: () => domainPurchasesApi.get(id!),
    enabled: id !== null,
    refetchInterval: (query) => {
      const view = query.state.data;
      if (view?.finished) return false;
      return view?.awaitingConfirmation ? 15_000 : 2_000;
    },
    refetchIntervalInBackground: false,
  });
}

export function useStartPurchase(callbacks?: MutationCallbacks<DomainPurchaseView, StartPurchaseRequest>) {
  return useApiMutation(domainPurchasesApi.start, () => [queryKeys.domainPurchases.all], callbacks);
}

export function useConfirmPurchase(id: string, callbacks?: MutationCallbacks<DomainPurchaseView, ConfirmPurchaseRequest>) {
  return useApiMutation(
    (input: ConfirmPurchaseRequest) => domainPurchasesApi.confirm(id, input),
    () => [queryKeys.domainPurchases.all, queryKeys.domainPurchases.one(id)],
    callbacks,
  );
}

export function useCancelPurchase(id: string, callbacks?: MutationCallbacks<DomainPurchaseView, void>) {
  return useApiMutation(
    () => domainPurchasesApi.cancel(id),
    () => [queryKeys.domainPurchases.all, queryKeys.domainPurchases.one(id)],
    callbacks,
  );
}
